<template>
  <div id="crew_membership">
    <v-row>
      <v-col>
        <h3>{{ $tc("memberRegistration.step_crew_membership.content.title") }}</h3>
        <p>{{ $tc("memberRegistration.step_crew_membership.content.description") }}</p>
      </v-col>
    </v-row>

    <div v-for="(participant, pIndex) in selectedParticipants" :key="participant.userId">
      <v-row v-if="selectedParticipants.length > 1" class="mb-1">
        <v-col>
          <h4>{{ participant.fName }} {{ participant.lName }}</h4>
        </v-col>
      </v-row>

      <!-- Selected member display -->
      <v-row v-if="memberSelections[participant.userId]">
        <v-col cols="12">
          <v-alert type="success" outlined dismissible @input="clearSelection(pIndex)">
            <strong>{{ $tc("memberRegistration.step_crew_membership.content.selected_member") }}:</strong>
            {{ getMemberName(participant.userId) }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Search bar -->
      <v-row>
        <v-col cols="12" md="8">
          <v-text-field
            v-model="searchQueries[pIndex]"
            :placeholder="$tc('memberRegistration.step_crew_membership.content.search_placeholder')"
            outlined
            dense
            prepend-inner-icon="mdi-magnify"
            :loading="searchLoading[pIndex]"
            @input="debounceSearch(pIndex)"
            clearable
          ></v-text-field>
        </v-col>
      </v-row>

      <!-- Search results -->
      <v-row v-if="searchResults[pIndex] && searchResults[pIndex].length > 0">
        <v-col
          v-for="member in searchResults[pIndex]"
          :key="'search-' + member.userId + '-' + pIndex"
          cols="12" sm="6" md="4"
        >
          <v-card
            outlined
            :class="['member-card', { 'member-card--selected': memberSelections[participant.userId] === member.userId }]"
            @click="selectMember(pIndex, member)"
          >
            <v-card-text class="d-flex align-center">
              <v-avatar size="36" class="mr-3">
                <v-img v-if="member.image" :src="member.image"></v-img>
                <v-icon v-else>mdi-account-circle</v-icon>
              </v-avatar>
              <span class="member-name">{{ member.firstName }} {{ member.lastName }}</span>
              <v-icon
                v-if="memberSelections[participant.userId] === member.userId"
                color="green"
                class="ml-auto"
              >mdi-check-circle</v-icon>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- No results message -->
      <v-row v-if="searchQueries[pIndex] && searchQueries[pIndex].length >= 2 && !searchLoading[pIndex] && searchResults[pIndex] && searchResults[pIndex].length === 0">
        <v-col>
          <v-alert type="info" outlined dense>
            {{ $tc("memberRegistration.step_crew_membership.content.no_results") }}
          </v-alert>
        </v-col>
      </v-row>

      <!-- Quick-select cards -->
      <template v-if="getOtherParticipants(pIndex).length > 0">
        <v-row class="mt-2">
          <v-col>
            <h5>{{ $tc("memberRegistration.step_crew_membership.content.quick_select_title") }}</h5>
          </v-col>
        </v-row>
        <v-row>
          <v-col
            v-for="other in getOtherParticipants(pIndex)"
            :key="'quick-' + other.userId + '-' + pIndex"
            cols="12" sm="6" md="4"
          >
            <v-card
              outlined
              :class="['member-card', { 'member-card--selected': memberSelections[participant.userId] === other.userId }]"
              @click="selectMemberFromParticipant(pIndex, other)"
            >
              <v-card-text class="d-flex align-center">
                <v-avatar size="36" class="mr-3">
                  <v-icon>mdi-account-circle</v-icon>
                </v-avatar>
                <span class="member-name">{{ other.fName }} {{ other.lName }}</span>
                <v-icon
                  v-if="memberSelections[participant.userId] === other.userId"
                  color="green"
                  class="ml-auto"
                >mdi-check-circle</v-icon>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
      </template>

      <!-- Relationship type dropdown -->
      <v-row v-if="memberSelections[participant.userId]" class="mt-3">
        <v-col cols="12" md="6">
          <v-select
            :items="relationshipCodes"
            item-text="name"
            item-value="id"
            :value="relationSelections[participant.userId]"
            @input="selectRelation(pIndex, $event)"
            :label="$tc('memberRegistration.step_crew_membership.content.relationship_label')"
            outlined
            dense
          ></v-select>
        </v-col>
      </v-row>

      <v-divider v-if="pIndex < selectedParticipants.length - 1" class="my-4"></v-divider>
    </div>

    <v-row class="mt-4">
      <v-col md="6" sm="6" cols="6" class="text-left">
        <v-btn :color="buttonColor" class="btn_next" @click="setStep(prevStep)">
          {{ $tc("registerEvent.prev") }}
        </v-btn>
      </v-col>
      <v-col md="6" sm="6" cols="6" class="text-right">
        <v-btn :color="buttonColor" class="btn_next" :disabled="!allSelectionsComplete" @click="setStep(nextStep)">
          {{ $tc("registerEvent.next") }}
        </v-btn>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import APIService from "@/services/APIService";
import {
  VRow, VCol, VBtn, VCard, VCardText, VIcon, VDivider, VAlert,
  VTextField, VSelect, VAvatar, VImg
} from "vuetify/lib";

export default {
  components: {
    VRow, VCol, VBtn, VCard, VCardText, VIcon, VDivider, VAlert,
    VTextField, VSelect, VAvatar, VImg,
  },
  props: {
    step: Number,
    buttonColor: String,
    previousStep: Number,
    nextStepValue: {
      type: Number,
      default: 4,
    },
    organizationId: [Number, String],
    selectedParticipants: Array,
    langCode: {
      type: Number,
      default: 1,
    },
  },
  data() {
    const memberSel = {};
    const relationSel = {};
    const memberNm = {};
    if (this.selectedParticipants) {
      this.selectedParticipants.forEach((p) => {
        memberSel[p.userId] = null;
        relationSel[p.userId] = null;
        memberNm[p.userId] = null;
      });
    }
    return {
      searchQueries: [],
      searchResults: [],
      searchLoading: [],
      searchTimers: [],
      memberSelections: memberSel,
      memberNames: memberNm,
      relationSelections: relationSel,
      relationshipCodes: [],
      codesLoaded: false,
    };
  },
  computed: {
    allSelectionsComplete() {
      if (!this.selectedParticipants || this.selectedParticipants.length === 0) return false;
      return this.selectedParticipants.every(
        (p) => this.memberSelections[p.userId] && this.relationSelections[p.userId]
      );
    },
    prevStep() {
      return this.previousStep;
    },
    nextStep() {
      return this.nextStepValue;
    },
  },
  watch: {
    step(val) {
      if (val === 3.3 && !this.codesLoaded) {
        this.fetchRelationshipCodes();
      }
    },
  },
  methods: {
    setStep(step) {
      this.$emit("nextStep", step);
    },
    getOtherParticipants(currentIndex) {
      return this.selectedParticipants.filter((_, i) => i !== currentIndex);
    },
    getMemberName(participantUserId) {
      return this.memberNames[participantUserId] || "";
    },
    selectMember(pIndex, member) {
      const participant = this.selectedParticipants[pIndex];
      const currentVal = this.memberSelections[participant.userId];
      if (currentVal === member.userId) {
        this.$set(this.memberSelections, participant.userId, null);
        this.$set(this.memberNames, participant.userId, null);
        this.$set(this.relationSelections, participant.userId, null);
        this.$set(participant, "associatedMemberId", null);
        this.$set(participant, "relationCodeId", null);
      } else {
        this.$set(this.memberSelections, participant.userId, member.userId);
        this.$set(this.memberNames, participant.userId, `${member.firstName} ${member.lastName}`);
        this.$set(participant, "associatedMemberId", member.userId);
      }
    },
    selectMemberFromParticipant(pIndex, other) {
      this.selectMember(pIndex, {
        userId: other.userId,
        firstName: other.fName,
        lastName: other.lName,
      });
    },
    selectRelation(pIndex, relationCodeId) {
      const participant = this.selectedParticipants[pIndex];
      this.$set(this.relationSelections, participant.userId, relationCodeId);
      this.$set(participant, "relationCodeId", relationCodeId);
    },
    clearSelection(pIndex) {
      const participant = this.selectedParticipants[pIndex];
      this.$set(this.memberSelections, participant.userId, null);
      this.$set(this.memberNames, participant.userId, null);
      this.$set(this.relationSelections, participant.userId, null);
      this.$set(participant, "associatedMemberId", null);
      this.$set(participant, "relationCodeId", null);
    },
    debounceSearch(pIndex) {
      if (this.searchTimers[pIndex]) {
        clearTimeout(this.searchTimers[pIndex]);
      }
      this.$set(this.searchTimers, pIndex, setTimeout(() => {
        this.searchMembers(pIndex);
      }, 400));
    },
    async searchMembers(pIndex) {
      const query = this.searchQueries[pIndex];
      if (!query || query.length < 2) {
        this.$set(this.searchResults, pIndex, []);
        return;
      }
      this.$set(this.searchLoading, pIndex, true);
      try {
        const resp = await APIService.fetch({
          path: `${this.$store.getters.env.get("VUE_APP_ROOT")}/laravel/public/api/v1/participant/organization/${this.organizationId}/search-members?q=${encodeURIComponent(query)}`,
        });
        if (resp.data.success) {
          this.$set(this.searchResults, pIndex, resp.data.message);
        }
      } catch (e) {
        // silenced
      } finally {
        this.$set(this.searchLoading, pIndex, false);
      }
    },
    async fetchRelationshipCodes() {
      try {
        const resp = await APIService.fetch({
          path: `${this.$store.getters.env.get("VUE_APP_ROOT")}/laravel/public/api/v1/participant/organization/${this.organizationId}/relationship-codes?lang=${this.langCode}`,
        });
        if (resp.data.success) {
          this.relationshipCodes = resp.data.message;
          this.codesLoaded = true;
        }
      } catch (e) {
        // silenced
      }
    },
  },
};
</script>

<style scoped>
.member-card {
  cursor: pointer;
  transition: border-color 0.2s, box-shadow 0.2s;
}
.member-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.12);
}
.member-card--selected {
  border: 2px solid #4caf50;
  background-color: #e8f5e9;
}
.member-name {
  font-weight: 600;
  font-size: 0.95rem;
}
</style>
